import { Button, HStack, useDisclosure } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { RiFilter3Line } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { SearchInput } from "src/components/Inputs/SearchInput";
import { Item } from "src/components/Inputs/SearchSelect/SearchSelect";
import { AdminPage } from "src/components/Layout/AdminPage";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { TabsWithSearchParams } from "src/components/Layout/TabsWithSearchParams";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { AdminHeading } from "src/components/Text/AdminHeading";
import { SearchAndFilterTypes } from "src/constants";
import {
  cancelPayment,
  listPayments,
  listPaymentTemplates,
  type ListPaymentsData,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import {
  useOrderByParams,
  usePaginationParams,
} from "src/hooks/useCommonSearchParams";
import { useDeleteConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import { usePaymentConfig } from "src/hooks/usePaymentConfig";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import * as breadcrumb from "src/services/breadcrumb";
import * as RD from "src/types/remoteData";
import { PaymentNotConfigured } from "../PaymentNotConfigured";
import { PaymentActivityList } from "./List";
import {
  DEFAULT_PAYMENT_STATUSES,
  PaymentActivityFilters,
} from "./PaymentActivityFilters";

export const PaymentActivity = () => {
  const organization = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const { orderBy, setOrderBy } = useOrderByParams();
  const { pagination, setPagination } = usePaginationParams();
  const queryClient = useQueryClient();
  const toast = useAvelaToast();
  const { confirm, confirmationDialog } = useDeleteConfirmationDialog({
    header: "Cancel payment",
    body: "Are you sure you want to cancel this payment? You can't undo this action afterwards.",
    confirmButton: {
      label: "Yes, cancel",
    },
    cancelButton: {
      label: "Keep payment",
    },
  });
  const searchTerm = searchParams.get(SearchAndFilterTypes.Search) ?? "";
  const { isOpen, onToggle } = useDisclosure();

  const organizationId = organization.map((org) => org.id).withDefault("");

  const templatesRD = useRemoteDataRestQuery({
    queryKey: ["paymentTemplates", organizationId],
    queryFn: () =>
      listPaymentTemplates({
        query: {
          organizationId,
          limit: 1000,
          offset: 0,
        },
      }).then((response) => response.data?.data),
    enabled: organization.hasData(),
  });

  const paymentTemplateItemsRD: RD.RemoteData<unknown, Item[]> =
    templatesRD.map((page) =>
      page.paymentTemplates.map((template) => ({
        key: template.id,
        label: template.name,
        description: template.description,
      }))
    );

  const paymentTemplateId = searchParams.get("paymentTemplateId");
  const statusFilter =
    searchParams.get("status") ?? DEFAULT_PAYMENT_STATUSES.join(",");
  const daysAgo = searchParams.get(SearchAndFilterTypes.DaysAgo);
  const dateFrom = searchParams.get(SearchAndFilterTypes.DateFrom);
  const dateTo = searchParams.get(SearchAndFilterTypes.DateTo);

  const paymentsRD = useRemoteDataRestQuery({
    queryKey: [
      "payments",
      organizationId,
      pagination.limit,
      pagination.offset,
      searchTerm,
      paymentTemplateId,
      statusFilter,
      daysAgo,
      dateFrom,
      dateTo,
      orderBy?.sortKey,
      orderBy?.sortType,
    ],
    queryFn: () =>
      listPayments({
        path: { organizationId },
        query: {
          limit: pagination.limit,
          offset: pagination.offset,
          search: searchTerm || undefined,
          paymentTemplateId: paymentTemplateId || undefined,
          status: statusFilter || undefined,
          daysAgo: daysAgo ? Number(daysAgo) : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          sortKey: orderBy?.sortKey as NonNullable<
            ListPaymentsData["query"]
          >["sortKey"],
          sortType: orderBy?.sortType as NonNullable<
            ListPaymentsData["query"]
          >["sortType"],
        },
      }).then((response) => response.data?.data),
    enabled: organization.hasData(),
  });

  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (searchParams.get("paymentTemplateId")) count++;
    if (searchParams.get("status")) count++;
    if (
      searchParams.get(SearchAndFilterTypes.DaysAgo) ||
      searchParams.get(SearchAndFilterTypes.DateFrom) ||
      searchParams.get(SearchAndFilterTypes.DateTo)
    ) {
      count++;
    }
    return count;
  }, [searchParams]);

  const handleActionSearch = useCallback(
    (searchTerm: string) => {
      if (!searchTerm) {
        searchParams.delete(SearchAndFilterTypes.Search);
      } else {
        searchParams.set(SearchAndFilterTypes.Search, searchTerm);
      }
      searchParams.delete(SearchAndFilterTypes.Offset);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleClearSearch = useCallback(
    () => handleActionSearch(""),
    [handleActionSearch]
  );

  const handleClearFilters = useCallback(() => {
    searchParams.delete("paymentTemplateId");
    searchParams.delete("status");
    searchParams.delete(SearchAndFilterTypes.DaysAgo);
    searchParams.delete(SearchAndFilterTypes.DateFrom);
    searchParams.delete(SearchAndFilterTypes.DateTo);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const handleCancel = useCallback(
    async (paymentId: string) => {
      if (!(await confirm())) return;

      try {
        await cancelPayment({ path: { paymentId } });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["payments"] }),
          queryClient.invalidateQueries({ queryKey: ["paymentTemplates"] }),
        ]);
      } catch (error) {
        console.error("Failed to cancel payment:", error);
        toast({
          title: "Error",
          description: "Failed to cancel payment",
          status: "error",
        });
      }
    },
    [confirm, queryClient, toast]
  );

  const buttonStyle =
    appliedFiltersCount > 0
      ? {
          variant: "outlineActivated" as const,
          colorScheme: "primary" as const,
        }
      : { variant: "outline" as const, colorScheme: "gray" as const };

  const paymentConfigRD = usePaymentConfig();

  return (
    <RemoteDataView
      remoteData={RD.toTuple3(organization, paymentsRD, paymentConfigRD)}
      notAsked={<Loading />}
      loading={<Loading />}
      error={() => <GenericError />}
      reloading={null}
    >
      {([, page, paymentConfig]) => {
        const tabs = [
          {
            key: "payments" as const,
            label: "Payments",
            isVisible: true,
            panel: (
              <>
                <HStack py={6}>
                  <SearchInput
                    onSearch={handleActionSearch}
                    value={searchParams.get(SearchAndFilterTypes.Search) ?? ""}
                    onClear={handleClearSearch}
                    placeholder="Search by student name or lastname, payment name"
                  />
                  <Button
                    {...buttonStyle}
                    leftIcon={<RiFilter3Line />}
                    onClick={onToggle}
                  >
                    Filter
                    {appliedFiltersCount > 0 && `: ${appliedFiltersCount}`}
                  </Button>
                </HStack>

                <PaymentActivityList
                  payments={page.payments}
                  limit={pagination.limit}
                  offset={pagination.offset}
                  count={page.total}
                  onFetchMore={setPagination}
                  onChangeSort={setOrderBy}
                  onClearSearch={() => {
                    handleClearSearch();
                    handleClearFilters();
                  }}
                  onCancel={handleCancel}
                  isLoading={paymentsRD.isLoading()}
                />

                <PaymentActivityFilters
                  isOpen={isOpen}
                  onToggleFilter={onToggle}
                  onClearFilters={handleClearFilters}
                  paymentTemplates={paymentTemplateItemsRD}
                />
                {confirmationDialog}
              </>
            ),
          },
          /*
          placeholder
          {
            key: "refunds" as const,
            label: "Refunds",
            isVisible: true,
            panel: <Flex>Refunds content</Flex>,
          },
          */
        ];

        return (
          <AdminPage
            breadcrumb={
              <Breadcrumb
                items={breadcrumb.payments.getBreadcrumbsForActivity(
                  organization
                )}
              />
            }
            heading={
              <>
                {!paymentConfig.isPaymentConfigured ? (
                  <PaymentNotConfigured />
                ) : null}
                <AdminHeading title="Payments activity" />
              </>
            }
          >
            <TabsWithSearchParams tabs={tabs} />
          </AdminPage>
        );
      }}
    </RemoteDataView>
  );
};
